import { type UseModalState } from "@clipboard-health/ui-react";
import CloseIcon from "@mui/icons-material/Close";
import { Box, Drawer as MuiDrawer, IconButton, type SxProps, type Theme } from "@mui/material";
import { mergeSxProps } from "@src/appV2/lib";

import { useIsMobile } from "../AppLayout/useResponsive";
import { Title } from "./Title";
import { useModalsContainerContext } from "./useModalsContainerContext";

/**
 * iOS-style transition configuration
 *
 * iOS uses spring animations with specific parameters. These timings
 * and curves are tuned to match iOS sheet presentation behavior.
 *
 * Reference: iOS UIKit spring animation with damping ratio ~0.85
 */
const DRAWER_TIMING = {
  // iOS sheets take ~350-400ms to fully present
  enter: 380,
  // iOS sheets dismiss slightly faster ~300ms
  exit: 300,
};

/**
 * iOS-authentic easing curves
 *
 * These curves approximate iOS spring physics:
 * - Enter: Smooth deceleration with natural settle (no bounce)
 * - Exit: Responsive start with smooth follow-through
 */
const DRAWER_EASING = {
  // iOS presentation curve - smooth spring deceleration
  // Approximates UISpringTimingParameters with damping 0.85
  enter: "cubic-bezier(0.2, 0.9, 0.3, 1)",
  // iOS dismissal curve - quick response, smooth finish
  exit: "cubic-bezier(0.3, 0, 0.5, 1)",
};

export interface DrawerProps {
  /**
   * The content of the drawer
   */
  children: React.ReactNode;
  /**
   * Modal state from useModalState hook
   */
  modalState: UseModalState;
  /**
   * Additional styles
   */
  sx?: SxProps<Theme>;
  /**
   * Optional title for the drawer header
   */
  title?: string;
  /**
   * Optional footer content (sticky at bottom)
   */
  footerContent?: React.ReactNode;
  /**
   * Show close button in header
   */
  showCloseButton?: boolean;
  /**
   * Ref to the scrollable content container
   */
  scrollContainerRef?: React.RefObject<HTMLDivElement>;
}

/**
 * Drawer component that appears on the right side.
 * On mobile, it displays as a fullscreen modal.
 */
export function Drawer(props: DrawerProps) {
  const { children, modalState, sx, title, footerContent, showCloseButton, scrollContainerRef } =
    props;
  const isMobile = useIsMobile();

  const { containerRef } = useModalsContainerContext();

  const container = containerRef?.current ?? null;

  // Show header if there's a title
  const hasHeader = Boolean(title);

  return (
    <MuiDrawer
      anchor="right"
      open={modalState.modalIsOpen}
      variant="temporary"
      transitionDuration={DRAWER_TIMING}
      SlideProps={{
        easing: DRAWER_EASING,
      }}
      ModalProps={{
        container: () => container,
        keepMounted: false,
        disableScrollLock: true,
        disableEnforceFocus: true,
        disableAutoFocus: true,
        disableRestoreFocus: true,
        // iOS-style backdrop: fades in sync with sheet movement
        BackdropProps: {
          transitionDuration: DRAWER_TIMING,
          sx: {
            // Smooth backdrop fade
            transition: `opacity ${DRAWER_TIMING.enter}ms ${DRAWER_EASING.enter}`,
            // Desktop: non-modal behavior (content behind is interactive)
            // Mobile: modal behavior (backdrop blocks interaction)
            ...(!isMobile && { pointerEvents: "none" }),
          },
        },
        ...(container && {
          style: { position: "absolute" },
        }),
      }}
      sx={mergeSxProps(
        (theme: Theme) => ({
          ...(container && {
            position: "absolute",
            // Use a high z-index to ensure the drawer overlays all content within the container
            zIndex: theme.zIndex.drawer,
          }),
          "& .MuiDrawer-paper": {
            width: isMobile ? "100%" : theme.size?.drawer.maxWidth,
            maxWidth: "100%",
            height: "100%",
            boxSizing: "border-box",
            padding: 0,
            boxShadow: theme.shadow?.drawer,
            display: "flex",
            flexDirection: "column",
            // GPU acceleration for smooth 60fps animations
            willChange: "transform",
            backfaceVisibility: "hidden",
            WebkitBackfaceVisibility: "hidden",
            ...(container && {
              position: "absolute",
            }),
          },
          "& .MuiBackdrop-root": {
            backgroundColor: "transparent",
            ...(container && {
              position: "absolute",
            }),
          },
        }),
        sx
      )}
      onClose={modalState.closeModal}
    >
      {hasHeader && (
        <Box
          sx={(theme) => ({
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: theme.spacing(5),
            flexShrink: 0,
            borderBottom: `1px solid ${theme.palette.divider}`,
          })}
        >
          <Title bold component="h6" variant="h6">
            {title}
          </Title>

          {showCloseButton && (
            <IconButton
              size="small"
              aria-label="Close drawer"
              sx={(theme) => ({
                marginLeft: theme.spacing(2),
              })}
              onClick={modalState.closeModal}
            >
              <CloseIcon />
            </IconButton>
          )}
        </Box>
      )}

      <Box
        ref={scrollContainerRef}
        sx={(theme) => ({
          flex: 1,
          overflowY: "auto",
          padding: theme.spacing(5),
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": {
            display: "none",
          },
        })}
      >
        {children}
      </Box>

      {footerContent && (
        <Box
          sx={(theme) => ({
            padding: theme.spacing(5),
            flexShrink: 0,
            borderTop: `1px solid ${theme.palette.divider}`,
          })}
        >
          {footerContent}
        </Box>
      )}
    </MuiDrawer>
  );
}
